import { Link } from "wouter";
import { ArrowRight, BellRing, CheckCircle2 } from "lucide-react";
import { HEALTH_REMINDERS_ENABLED } from "@shared/constants/features";
import { useNotifications, useMarkRead } from "../../lib/api/useNotifications";
import { NotificationList } from "../notifications/NotificationList";
import { SectionCard } from "../ui/SectionCard";
import { EmptyState } from "../ui/EmptyState";
import { Skeleton } from "../ui/Skeleton";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";

const MAX_ITEMS = 5;

/**
 * Inline "recent activity" feed on the dashboard: the top few notifications,
 * mark-read on click. Shares the ["notifications"] query with the header bell
 * and the notification center (one fetch, one source of truth), so marking one
 * read here updates the badge everywhere. This is NOT the header bell — it's the
 * inline list, capped at MAX_ITEMS with a "View all" link to the full center.
 */
export function RecentActivity({ className }: { className?: string }) {
  const { data, isLoading, error } = useNotifications();
  const markRead = useMarkRead();

  const items = (data?.items ?? []).slice(0, MAX_ITEMS);
  const hasItems = items.length > 0;

  return (
    <SectionCard
      icon={BellRing}
      title="Recent activity & alerts"
      accent="sky"
      className={className}
      actions={
        <Link
          href="/notifications"
          className={cn(
            "inline-flex items-center gap-1 rounded-full text-sm font-semibold text-sky-700 transition hover:text-sky-900",
            FOCUS_RING,
          )}
        >
          View all
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
      }
    >
      {isLoading && <SkeletonList />}

      {error && (
        <p role="alert" className="rounded-2xl bg-red-50 p-4 text-sm text-red-700">
          {error.message}
        </p>
      )}

      {!isLoading && !error && !hasItems && (
        <EmptyState
          size="sm"
          icon={CheckCircle2}
          accent="emerald"
          title="You're all caught up."
          description={
            HEALTH_REMINDERS_ENABLED
              ? "Link opens and medication reminders will show up here."
              : "When a caregiver opens a link you shared, it'll show up here."
          }
        />
      )}

      {!isLoading && !error && hasItems && (
        <NotificationList notifications={items} onMarkRead={(id) => markRead.mutate(id)} />
      )}
    </SectionCard>
  );
}

function SkeletonList() {
  return (
    <ul className="space-y-2" aria-hidden>
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex items-start gap-3 rounded-2xl bg-white/60 px-4 py-3 ring-1 ring-white/80">
          <Skeleton className="mt-1.5 h-2.5 w-2.5 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
        </li>
      ))}
    </ul>
  );
}
